import type {
  NodeAccomodationFragment,
  NodeEditorialFragment,
  NodePlaceFragment,
  NodeSinglePageApplicationFragment,
  NodeTourFragment,
  TaxonomyTermGeographicalRegionsFragment,
  WebformFragment,
} from '#graphql-operations'
import type { Query } from '#nuxt-graphql-middleware/operation-types'

/**
 * The available node bundles and their corresponding GraphQL fragment.
 */
type RouteQueryMap = {
  all:
    | NodeEditorialFragment
    | NodeSinglePageApplicationFragment
    | NodePlaceFragment
    | NodeAccomodationFragment
    | NodeTourFragment
  editorial: NodeEditorialFragment
  singlePageApplication: NodeSinglePageApplicationFragment
  taxonomyTermGeographicalRegions: TaxonomyTermGeographicalRegionsFragment
  webform: WebformFragment
}

type ValidRouteQueries = keyof Pick<
  Query,
  | 'routeNodeEditorial'
  | 'routeNodeSinglePageApplication'
  | 'route'
  | 'routeTermGeographicalRegion'
  | 'routeWebform'
>

type UseEditorialRouteQuery<T> = {
  entity: ComputedRef<T>
}

function getQueryName(id: keyof RouteQueryMap): ValidRouteQueries {
  if (id === 'singlePageApplication') {
    return 'routeNodeSinglePageApplication'
  } else if (id === 'editorial') {
    return 'routeNodeEditorial'
  } else if (id === 'all') {
    return 'route'
  } else if (id === 'taxonomyTermGeographicalRegions') {
    return 'routeTermGeographicalRegion'
  } else if (id === 'webform') {
    return 'routeWebform'
  }

  throw new Error('Unknown route query bundle: ' + id)
}

/**
 * Performs a route query for the given node bundle.
 */
export default async function <
  T extends keyof RouteQueryMap,
  V = RouteQueryMap[T],
>(id: T): Promise<UseEditorialRouteQuery<V>> {
  const route = useRoute()

  const { data: query } = await useAsyncGraphqlQuery(
    getQueryName(id),
    {
      path: route.path,
    },
    {
      transform: function (data) {
        return data.data
      },
      deep: false,
      graphqlCaching: {
        client: true,
      },
    },
  )

  const { entity, metatags } = await useDrupalRoute<V>(query.value)

  useDrupalLanguageLinks().setLanguageLinks(query.value)
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  useLogo().setLogoThemeFragment(entity as any)
  useBreadcrumb().setLinksFromRoute(query.value)

  // The generic type V for the fragment can't be made compatible with these
  // argument type, which is why we have to cast is as any here (which is fine).
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  usePageTracking(entity as any, metatags)
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  setActiveChannel(entity as any)

  return {
    entity,
  }
}
